import React from 'react';

/**
 * Paginierung wie in der Organisationsansicht (z. B. Mitarbeiter verwalten):
 * Info links, Zurück + Seitenzahlen + Weiter in der Mitte, „pro Seite“ rechts.
 */
function OrgStylePagination({
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  perPage = 25,
  onPageChange,
  onPerPageChange,
  perPageOptions = [10, 25, 50, 100, 150, 200, 500, 1000],
  countSingular = 'Eintrag',
  countPlural = 'Einträge',
  perPageLabel = 'Einträge pro Seite:',
}) {
  if (totalCount < 1) {
    return null;
  }

  const tp = Math.max(1, totalPages || 1);
  const hasPrev = currentPage > 1;
  const hasNext = currentPage < tp;
  const page = Math.min(Math.max(1, currentPage), tp);

  return (
    <div className="org-pagination-wrap">
      <style jsx>{`
        .org-pagination-wrap .pagination-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          margin-top: 20px;
          margin-bottom: 20px;
        }
        .org-pagination-wrap .pagination-info {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.875rem;
          color: #4a5568;
        }
        .org-pagination-wrap .pagination-controls {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .org-pagination-wrap .pagination-btn {
          padding: 8px 16px;
          background: #4a6de5;
          color: white !important;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: background-color 0.2s;
        }
        .org-pagination-wrap .pagination-btn:hover:not(:disabled) {
          background: #3c5ce0;
        }
        .org-pagination-wrap .pagination-btn:disabled {
          background: #cbd5e0;
          color: #4a5568 !important;
          cursor: not-allowed;
        }
        .org-pagination-wrap .page-numbers {
          display: flex;
          gap: 4px;
        }
        .org-pagination-wrap .page-number {
          padding: 8px 12px;
          background: #f7fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          color: #2d3748;
          transition: all 0.2s;
        }
        .org-pagination-wrap .page-number:hover {
          background: #edf2f7;
          border-color: #cbd5e0;
        }
        .org-pagination-wrap .page-number.active {
          background: #4a6de5;
          color: white;
          border-color: #4a6de5;
        }
        .org-pagination-wrap .items-per-page {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .org-pagination-wrap .items-per-page label {
          font-size: 0.875rem;
          color: #4a5568;
        }
        .org-pagination-wrap .items-per-page select {
          padding: 6px 10px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 0.875rem;
          background: white;
          color: #1a202c;
        }
        @media (max-width: 768px) {
          .org-pagination-wrap .pagination-container {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }
          .org-pagination-wrap .pagination-controls {
            order: 1;
          }
          .org-pagination-wrap .pagination-info {
            order: 2;
          }
          .org-pagination-wrap .items-per-page {
            order: 3;
          }
        }
      `}</style>
      <div className="pagination-container">
        <div className="pagination-info">
          <span>
            Seite {page} von {tp}
          </span>
          <span>•</span>
          <span>
            {totalCount} {totalCount === 1 ? countSingular : countPlural} insgesamt
          </span>
        </div>

        <div className="pagination-controls">
          <button
            type="button"
            className="pagination-btn"
            onClick={() => onPageChange && onPageChange(page - 1)}
            disabled={!hasPrev}
          >
            ← Zurück
          </button>
          <div className="page-numbers">
            {Array.from({ length: Math.min(5, tp) }, (_, i) => {
              let pageNum;
              if (tp <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= tp - 2) {
                pageNum = tp - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <button
                  type="button"
                  key={pageNum}
                  className={`page-number ${page === pageNum ? 'active' : ''}`}
                  onClick={() => onPageChange && onPageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="pagination-btn"
            onClick={() => onPageChange && onPageChange(page + 1)}
            disabled={!hasNext}
          >
            Weiter →
          </button>
        </div>

        <div className="items-per-page">
          <label htmlFor="org-style-pagination-per-page">{perPageLabel}</label>
          <select
            id="org-style-pagination-per-page"
            value={perPage}
            onChange={(e) => onPerPageChange && onPerPageChange(parseInt(e.target.value, 10))}
          >
            {perPageOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

export default OrgStylePagination;
